import { Bell } from "lucide-react";
import { EmptyState } from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  useNotifications,
  useUnreadNotificationsCount,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
} from "@/hooks/useNotifications";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

// Types are written by DB triggers — see notify_new_lead (20260208003904),
// auto_create_portfolio_draft (20260616141906) and the flow triggers in
// 20260912100000_flow_notifications.sql.
const typeIcons: Record<string, string> = {
  new_lead: "📩",
  lead_updated: "📝",
  project_due: "⏰",
  comment: "💬",
  proposal_viewed: "👀",
  proposal_signed: "✅",
  proposal_rejected: "❌",
  contract_viewed: "👀",
  contract_signed: "📝",
  contract_rejected: "❌",
  payment_received: "💰",
  payment_overdue: "⚠️",
  testimonial_received: "⭐",
  portfolio_draft: "🗂️",
};

export function NotificationsDropdown() {
  const navigate = useNavigate();
  const { data: notifications, isLoading } = useNotifications();
  const unreadCount = useUnreadNotificationsCount();
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();

  const handleNotificationClick = (notification: {
    id: string;
    link: string | null;
    read_at: string | null;
  }) => {
    if (!notification.read_at) {
      markAsRead.mutate(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="relative h-9 w-9 rounded-[9px] border-border bg-card text-muted-foreground hover:text-foreground"
          aria-label="Notificações"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute right-2 top-[7px] h-[7px] w-[7px] rounded-full border-[1.5px] border-card bg-bad" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[370px] max-w-[92vw] overflow-hidden rounded-2xl p-0 shadow-elegant"
      >
        <DropdownMenuLabel className="flex items-center justify-between border-b border-border px-[18px] py-[15px]">
          <span className="text-sm font-bold">Notificações</span>
          {unreadCount > 0 && (
            <button
              type="button"
              className="text-[11.5px] font-semibold text-primary hover:underline"
              onClick={() => markAllAsRead.mutate()}
            >
              Marcar todas
            </button>
          )}
        </DropdownMenuLabel>

        <ScrollArea className="max-h-[340px]">
          {isLoading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">Carregando...</div>
          ) : notifications?.length === 0 ? (
            <EmptyState size="sm" title="Nenhuma notificação" className="py-4" />
          ) : (
            notifications?.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={`flex cursor-pointer items-start gap-[11px] rounded-none border-b border-border px-[18px] py-[13px] last:border-b-0 ${
                  !notification.read_at ? "bg-accent/60" : ""
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <span className="text-[15px] leading-[1.2]">
                  {typeIcons[notification.type] || "🔔"}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-semibold leading-[1.4]">
                    {notification.title}
                  </p>
                  <p className="mt-0.5 truncate text-[11.5px] text-muted-foreground">
                    {notification.message ? `${notification.message} · ` : ""}
                    {formatDistanceToNow(new Date(notification.created_at), {
                      addSuffix: true,
                      locale: ptBR,
                    })}
                  </p>
                </div>
                <span
                  className={`mt-[5px] h-[7px] w-[7px] shrink-0 rounded-full bg-primary ${
                    notification.read_at ? "opacity-0" : ""
                  }`}
                />
              </DropdownMenuItem>
            ))
          )}
        </ScrollArea>

        <button
          type="button"
          className="w-full px-[18px] py-3 text-center text-xs font-semibold text-primary transition-colors hover:bg-muted/60"
          onClick={() => navigate("/admin")}
        >
          Ver todas
        </button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
